/* eslint-disable import/no-named-as-default */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { INITIAL_MENU_STATE } from '../../main-menu/menu.slice';
import MapLayer from '../../map/map-layer-manager/map-layer.enum';
import { INITIAL_MAP_STATE } from '../../map/map.slice';
import { HistoryFormValues } from '../../maritime-menu-options/history-panel/history-form/history-form-validators';
import { INITIAL_HISTORY_PANEL_STATE } from '../../maritime-menu-options/history-panel/history-panel.slice';
import { defaultIncidentFilters } from '../../maritime-menu-options/incidents-panel/incident-panel.slice';

import {
  DEFAULT_PORT_FILTERS,
  PortsFilters,
} from '../../maritime-menu-options/world-ports-panel/world-ports-panel.slice';

import { IncidentFilters } from '../../maritime-menu-options/incidents-panel/incident.model';
import { UserPreferencesBaseMap } from '../../utils/user.enum';

export type UserPreferences = {
  basemap: UserPreferencesBaseMap;
  showRiNewsBanner: boolean;
  showDatumRings: boolean;
  zoomToIncident: boolean;
  mapExtent?: {
    center: [number, number];
    zoom: number;
  };
  menu?: {
    menuOpen: boolean;
    selectedOption: string;
    secondaryMenuOpen: boolean;
    selectedDossier: string;
    threatAssessmentOpen: boolean;
  };
  alertsPanel?: {
    layers: MapLayer[];
    disabled: boolean;
  };
  drawingsPanel?: {
    layers: MapLayer[];
    disabled: boolean;
  };
  routesPanel?: {
    layers: MapLayer[];
    disabled: boolean;
  };
  historyPanel?: {
    formValues: HistoryFormValues;
    layers: MapLayer[];
    disabled: boolean;
  };
  RIMaritimeAreasPanel?: {
    layers: MapLayer[];
    disabled: boolean;
  };
  openSeaMapsPanel?: {
    layers: MapLayer[];
    disabled: boolean;
  };
  incidentsPanel?: {
    filters: IncidentFilters;
    layers: MapLayer[];
    disabled: boolean;
  };
  boundariesPanel?: {
    layers: string[];
    disabled: boolean;
  };
  worldPortsPanel?: {
    layers: MapLayer[];
    disabled: boolean;
    filters: PortsFilters;
  };
};

type UserPreferencesInitialState = {
  userPreferences: UserPreferences;
  userPreferencesLoading: boolean;
};
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  basemap: UserPreferencesBaseMap.DEFAULT,
  showRiNewsBanner: false,
  showDatumRings: true,
  zoomToIncident: true,
  mapExtent: {
    center: INITIAL_MAP_STATE.mapExtent.center,
    zoom: INITIAL_MAP_STATE.mapExtent.zoom,
  },
  menu: {
    ...INITIAL_MENU_STATE,
  },
  alertsPanel: {
    layers: [] as MapLayer[],
    disabled: false,
  },
  drawingsPanel: {
    layers: [] as MapLayer[],
    disabled: false,
  },
  historyPanel: {
    formValues: INITIAL_HISTORY_PANEL_STATE.formValues,
    layers: [] as MapLayer[],
    disabled: false,
  },
  openSeaMapsPanel: {
    layers: [] as MapLayer[],
    disabled: false,
  },
  incidentsPanel: {
    filters: defaultIncidentFilters,
    layers: [] as MapLayer[],
    disabled: false,
  },
  boundariesPanel: {
    layers: [] as MapLayer[],
    disabled: false,
  },
  worldPortsPanel: {
    layers: [] as MapLayer[],
    disabled: false,
    filters: DEFAULT_PORT_FILTERS,
  },
};

export const INITIAL_USER_PREFERENCES_STATE: UserPreferencesInitialState = {
  userPreferences: {
    ...DEFAULT_USER_PREFERENCES,
  },
  userPreferencesLoading: false,
};

const UserPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState: INITIAL_USER_PREFERENCES_STATE,
  reducers: {
    setUserPreferences: (state, action: PayloadAction<UserPreferences>) => {
      state.userPreferences = action.payload;
    },
    setUserPreferencesLoading: (state, action) => {
      state.userPreferencesLoading = action.payload;
    },
    setShowRiNewsBanner: (state, action) => {
      state.userPreferences.showRiNewsBanner = action.payload;
    },
    setShowDatumRings: (state, action) => {
      state.userPreferences.showDatumRings = action.payload;
    },
    setZoomToIncident: (state, action) => {
      state.userPreferences.zoomToIncident = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function

export const {
  setUserPreferences,
  setUserPreferencesLoading,
  setShowRiNewsBanner,
  setShowDatumRings,
  setZoomToIncident,
} = UserPreferencesSlice.actions;

export default UserPreferencesSlice.reducer;
